import { useState, useMemo, useCallback } from 'react';
import { adminContests, contestTypeLabels, contestTypeIcons, contestTypeColors, contestStatusLabels, contestStatusColors, mockWinnerNames, getContestParticipants, type Contest, type ContestType, type ContestStatus } from '@/mocks/adminContests';
import { useReminders } from '@/hooks/useReminders';
import { SwalAlert } from '@/hooks/useSweetAlert';

const PARTICIPATE_KEY = 'rfw_app_contest_participate';
const VOTE_KEY = 'rfw_app_contest_vote';

function loadSet(key: string): Set<number> { try { const raw = localStorage.getItem(key); return raw ? new Set(JSON.parse(raw)) : new Set(); } catch { return new Set(); } }
function saveSet(key: string, s: Set<number>) { localStorage.setItem(key, JSON.stringify([...s])); }

type StatusFilter = 'todos' | 'activo' | 'proximamente' | 'finalizado';

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AppContestsTab() {
  const [typeFilter, setTypeFilter] = useState<ContestType | 'todos'>('todos');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  const [selectedContest, setSelectedContest] = useState<Contest | null>(null);
  const [detailTab, setDetailTab] = useState<'info' | 'rules' | 'winner'>('info');
  const [participateSet, setParticipateSet] = useState<Set<number>>(() => loadSet(PARTICIPATE_KEY));
  const [voteSet, setVoteSet] = useState<Set<number>>(() => loadSet(VOTE_KEY));
  const { hasReminder, toggleReminder } = useReminders();

  const types: ContestType[] = ['sorteo', 'trivia', 'comentarios', 'compartir', 'participacion'];

  const filtered = useMemo(() => {
    let result = [...adminContests];
    if (typeFilter !== 'todos') result = result.filter(c => c.type === typeFilter);
    if (statusFilter === 'activo') result = result.filter(c => c.status === 'activo');
    else if (statusFilter === 'proximamente') result = result.filter(c => c.status === 'proximamente');
    else if (statusFilter === 'finalizado') result = result.filter(c => c.status === 'finalizado');
    else result = result.filter(c => c.status !== 'pausado');
    result.sort((a, b) => {
      const order: Record<string, number> = { activo: 0, proximamente: 1, pausado: 2, finalizado: 3 };
      return order[a.status] - order[b.status];
    });
    return result;
  }, [typeFilter, statusFilter]);

  const toggleParticipate = useCallback((id: number) => {
    setParticipateSet(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); saveSet(PARTICIPATE_KEY, next); return next; });
  }, []);

  const toggleVote = useCallback((id: number) => {
    setVoteSet(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); saveSet(VOTE_KEY, next); return next; });
  }, []);

  const handleShare = useCallback((contest: Contest) => {
    const url = `${window.location.origin}/contests/${contest.id}`;
    if (navigator.share) { navigator.share({ title: contest.title, text: contest.description.slice(0, 100), url }); }
    else { navigator.clipboard.writeText(url).then(() => SwalAlert.toast('Link copiado', 'success')); }
  }, []);

  const openDetail = useCallback((contest: Contest, tab: 'info' | 'rules' | 'winner' = 'info') => {
    setSelectedContest(contest);
    setDetailTab(tab);
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center">
            <i className="ri-trophy-line text-rose-600 text-lg"></i>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-foreground-950">Concursos</h2>
            <p className="text-[11px] text-foreground-400">{adminContests.filter(c => c.status === 'activo').length} activos · {adminContests.length} totales</p>
          </div>
        </div>

        {/* Status filters */}
        <div className="flex gap-1.5 mb-3">
          {(['todos', 'activo', 'proximamente', 'finalizado'] as StatusFilter[]).map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                statusFilter === s ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}>
              {s === 'todos' ? 'Todos' : s === 'activo' ? 'Activos' : s === 'proximamente' ? 'Próximos' : 'Finalizados'}
            </button>
          ))}
        </div>

        {/* Type chips */}
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setTypeFilter('todos')}
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer ${
              typeFilter === 'todos' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500'
            }`}>Todos</button>
          {types.map(t => {
            const c = contestTypeColors[t];
            return (
              <button key={t} onClick={() => setTypeFilter(typeFilter === t ? 'todos' : t)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  typeFilter === t ? `${c.bg} ${c.text} border ${c.border}` : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}>
                <i className={`${contestTypeIcons[t]} text-[11px]`}></i>{contestTypeLabels[t]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-2">
        <p className="text-[11px] text-foreground-400">{filtered.length} concurso{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="px-3 pb-6 space-y-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-trophy-line text-xl text-foreground-300"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-400">Sin concursos</p>
            <p className="text-[11px] text-foreground-300">Intentá ajustar los filtros</p>
          </div>
        ) : filtered.map(contest => {
          const typeColors = contestTypeColors[contest.type];
          const statusColors = contestStatusColors[contest.status];
          const isParticipated = participateSet.has(contest.id);
          const isVoted = voteSet.has(contest.id);
          const reminded = hasReminder(contest.id, 'contest');
          const isActive = contest.status === 'activo';

          return (
            <div key={contest.id} onClick={() => openDetail(contest)}
              className={`bg-background-50 border rounded-2xl overflow-hidden transition-all cursor-pointer ${
                contest.status === 'finalizado' ? 'border-background-200/70 opacity-85' : 'border-background-200 hover:border-foreground-300'
              }`}>
              <div className="p-3.5">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-semibold ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
                    <i className={`${contestTypeIcons[contest.type]} text-[8px]`}></i>{contestTypeLabels[contest.type]}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-semibold ${statusColors.bg} ${statusColors.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusColors.dot}`}></span>{contestStatusLabels[contest.status]}
                  </span>
                  {contest.prize && <span className="text-[9px] font-bold text-amber-600">{contest.prize.length > 40 ? contest.prize.slice(0, 40) + '...' : contest.prize}</span>}
                </div>
                <h3 className="text-sm font-bold text-foreground-950 leading-snug mb-1">{contest.title}</h3>
                <p className="text-[11px] text-foreground-500 leading-relaxed line-clamp-2 mb-2">{contest.description}</p>
                <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                  <span className="flex items-center gap-1"><i className="ri-calendar-line text-[10px]"></i>{formatDate(contest.startDate)}</span>
                  <span className="flex items-center gap-1"><i className="ri-group-line text-[10px]"></i>{contest.participants.toLocaleString()} part.</span>
                  {contest.winnerName && <span className="flex items-center gap-1 text-amber-600 font-semibold"><i className="ri-trophy-line text-[10px]"></i>{contest.winnerName}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 px-3 pb-3" onClick={e => e.stopPropagation()}>
                {isActive && (
                  <button onClick={() => { toggleParticipate(contest.id); SwalAlert.toast(isParticipated ? 'Participación cancelada' : '¡Participando!', isParticipated ? 'info' : 'success'); }}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      isParticipated ? 'bg-emerald-100 text-emerald-700' : 'bg-background-100 text-foreground-500 hover:bg-emerald-50'
                    }`}>
                    <i className={`text-[10px] ${isParticipated ? 'ri-check-double-fill' : 'ri-user-star-line'}`}></i>
                    {isParticipated ? 'Participando' : 'Participar'}
                  </button>
                )}
                {isActive && contest.type === 'trivia' && (
                  <button onClick={() => { if (!isVoted) { toggleVote(contest.id); SwalAlert.toast('¡Voto registrado!', 'success'); } else SwalAlert.toast('Ya votaste', 'warning'); }}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      isVoted ? 'bg-violet-100 text-violet-700' : 'bg-background-100 text-foreground-500 hover:bg-violet-50'
                    }`}>
                    <i className="ri-check-line text-[10px]"></i>Votar
                  </button>
                )}
                <button onClick={() => openDetail(contest, 'rules')}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-background-100 text-foreground-500 hover:bg-sky-50 whitespace-nowrap cursor-pointer">
                  <i className="ri-file-text-line text-[10px]"></i>Reglas
                </button>
                {contest.winnerName && (
                  <button onClick={() => openDetail(contest, 'winner')}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 whitespace-nowrap cursor-pointer">
                    <i className="ri-trophy-line text-[10px]"></i>Ganador
                  </button>
                )}
                <button onClick={() => handleShare(contest)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-background-100 text-foreground-500 hover:bg-violet-50 whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line text-[10px]"></i>
                </button>
                <div className="flex-1"></div>
                {isActive && (
                  <button onClick={() => {
                    const added = toggleReminder(contest.id, 'contest', contest.title, contest.endDate);
                    SwalAlert.toast(added ? 'Recordatorio activado' : 'Recordatorio cancelado', added ? 'success' : 'info');
                  }}
                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      reminded ? 'bg-amber-100 text-amber-600' : 'bg-background-100 text-foreground-400'
                    }`}>
                    <i className={`${reminded ? 'ri-notification-3-fill' : 'ri-notification-3-line'} text-[12px]`}></i>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── DETAIL MODAL ─── */}
      {selectedContest && (() => {
        const contest = selectedContest;
        const typeColors = contestTypeColors[contest.type];
        const isParticipated = participateSet.has(contest.id);
        const reminded = hasReminder(contest.id, 'contest');
        const isActive = contest.status === 'activo';
        const participants = contest.participants > 0 ? getContestParticipants(contest.id) : [];

        return (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedContest(null)}></div>
            <div className="relative bg-background-50 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-[slideUp_0.2s_ease-out]">
              <button onClick={() => setSelectedContest(null)} className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-foreground-700"></i>
              </button>

              <div className="pt-6 px-4">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-semibold ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
                    <i className={`${contestTypeIcons[contest.type]} text-[8px]`}></i>{contestTypeLabels[contest.type]}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-semibold ${contestStatusColors[contest.status].bg} ${contestStatusColors[contest.status].text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${contestStatusColors[contest.status].dot}`}></span>{contestStatusLabels[contest.status]}
                  </span>
                </div>
                <h2 className="text-lg font-extrabold text-foreground-950 mb-2">{contest.title}</h2>

                {/* Detail tabs */}
                <div className="flex items-center border-b border-background-200/70 mb-3">
                  {[
                    { id: 'info' as const, label: 'Información', icon: 'ri-information-line' },
                    { id: 'rules' as const, label: 'Reglas', icon: 'ri-file-text-line' },
                    { id: 'winner' as const, label: 'Ganador', icon: 'ri-trophy-line' },
                  ].map(tab => (
                    <button key={tab.id} onClick={() => setDetailTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        detailTab === tab.id ? 'border-secondary-500 text-secondary-600' : 'border-transparent text-foreground-400 hover:text-foreground-600'
                      }`}>
                      <i className={`${tab.icon} text-[11px]`}></i>{tab.label}
                    </button>
                  ))}
                </div>

                {detailTab === 'info' && (
                  <div className="space-y-3 pb-4">
                    <p className="text-sm text-foreground-700 leading-relaxed">{contest.description}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2.5 rounded-xl bg-background-100">
                        <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Inicio</p>
                        <p className="text-xs font-bold text-foreground-900">{formatDate(contest.startDate)}</p>
                      </div>
                      <div className="p-2.5 rounded-xl bg-background-100">
                        <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Fin</p>
                        <p className="text-xs font-bold text-foreground-900">{formatDate(contest.endDate)}</p>
                      </div>
                      <div className="p-2.5 rounded-xl bg-background-100">
                        <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Premio</p>
                        <p className="text-[11px] font-bold text-amber-600">{contest.prize}</p>
                      </div>
                      <div className="p-2.5 rounded-xl bg-background-100">
                        <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Participantes</p>
                        <p className="text-sm font-bold text-foreground-900">{contest.participants.toLocaleString()}</p>
                      </div>
                    </div>
                    {participants.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-foreground-700 mb-2">Participantes recientes</p>
                        <div className="space-y-2">
                          {participants.slice(0, 5).map(p => (
                            <div key={p.id} className="flex items-center gap-2 p-2 rounded-lg bg-background-100">
                              <img src={p.avatar} alt={p.name} className="w-7 h-7 rounded-full object-cover" />
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-semibold text-foreground-800 truncate">{p.name}</p>
                                <p className="text-[9px] text-foreground-400">{p.entry.slice(0, 50)}...</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {detailTab === 'rules' && (
                  <div className="space-y-3 pb-4">
                    <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">{contest.rules}</p>
                  </div>
                )}

                {detailTab === 'winner' && (
                  <div className="space-y-3 pb-4">
                    {contest.winnerName ? (
                      <div className="flex flex-col items-center text-center p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-amber-100 mb-2">
                          <img src={contest.winnerAvatar || 'https://readdy.ai/api/search-image?query=Default%20winner%20avatar%20trophy%20celebration%20concept%2C%20golden%20confetti%20and%20warm%20amber%20tones%2C%20celebration%20atmosphere&width=160&height=160&seq=winner-default&orientation=squarish'} alt={contest.winnerName} className="w-full h-full object-cover" />
                        </div>
                        <p className="text-sm font-extrabold text-amber-800">{contest.winnerName}</p>
                        <p className="text-[11px] text-amber-600 font-semibold mt-1">¡Ganador de "{contest.title}"!</p>
                        <p className="text-[11px] text-foreground-600 mt-2">{contest.prize}</p>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
                          <i className="ri-question-line text-xl text-foreground-300"></i>
                        </div>
                        <p className="text-sm font-semibold text-foreground-400">Sin ganador anunciado</p>
                        <p className="text-[11px] text-foreground-300">El ganador se anunciará cuando finalice el concurso.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Bottom actions */}
                <div className="flex flex-wrap gap-2 py-3 border-t border-background-100">
                  {isActive && (
                    <button onClick={() => { toggleParticipate(contest.id); SwalAlert.toast(isParticipated ? 'Participación cancelada' : '¡Participando!', isParticipated ? 'info' : 'success'); }}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                        isParticipated ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                      }`}>
                      <i className={`text-[11px] ${isParticipated ? 'ri-check-double-fill' : 'ri-user-star-line'}`}></i>
                      {isParticipated ? 'Participando' : 'Participar'}
                    </button>
                  )}
                  <button onClick={() => handleShare(contest)} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-violet-50 text-violet-700 hover:bg-violet-100 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line text-[11px]"></i>Compartir
                  </button>
                  {isActive && (
                    <button onClick={() => {
                      const added = toggleReminder(contest.id, 'contest', contest.title, contest.endDate);
                      SwalAlert.toast(added ? 'Recordatorio activado' : 'Recordatorio cancelado', added ? 'success' : 'info');
                    }}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                        reminded ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                      }`}>
                      <i className={`text-[11px] ${reminded ? 'ri-notification-3-fill' : 'ri-notification-3-line'}`}></i>
                      {reminded ? 'Recordado' : 'Recordarme'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}